import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Upload, Image as ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';
import Alert from './Alert';
import { getProductImageUrl as getConfigProductImageUrl, API_BASE_URL, IMAGE_BASE_URL } from '../config';
import { Editor } from '@tinymce/tinymce-react';

const sanitizeNumericValue = (value, { integer = false } = {}) => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return integer ? Math.trunc(parsed) : parsed;
};

const normalizeVariantValues = (values) => {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set();
  return values.reduce((acc, value) => {
    if (value === null || typeof value === 'undefined') {
      return acc;
    }

    const stringValue = typeof value === 'string' ? value.trim() : String(value);

    if (!stringValue || seen.has(stringValue)) {
      return acc;
    }

    seen.add(stringValue);
    acc.push(stringValue);
    return acc;
  }, []);
};

const buildVariantCombinationKey = (options = {}) => {
  if (!options || typeof options !== 'object') {
    return '';
  }

  return Object.keys(options)
    .sort()
    .map((key) => `${key}:${options[key]}`)
    .join('|');
};

const sanitizeImagePath = (path) => {
  if (!path || typeof path !== 'string') {
    return '';
  }

  const [cleanPath] = path.split('?');
  return cleanPath;
};

const normalizeVariantItems = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => {
      const rawOptions = item.options && typeof item.options === 'object' ? item.options : {};
      const normalizedOptions = Object.entries(rawOptions).reduce((acc, [key, value]) => {
        if (!key) {
          return acc;
        }
        const trimmedKey = typeof key === 'string' ? key.trim() : String(key);
        const trimmedValue = value === null || typeof value === 'undefined' ? '' : (typeof value === 'string' ? value.trim() : String(value));
        if (!trimmedKey || !trimmedValue) {
          return acc;
        }
        acc[trimmedKey] = trimmedValue;
        return acc;
      }, {});

      const combinationKey = buildVariantCombinationKey(normalizedOptions);
      const rawImageVersion = typeof item.image_version !== 'undefined' ? item.image_version : (typeof item.imageVersion !== 'undefined' ? item.imageVersion : null);
      const normalizedImageVersion = sanitizeNumericValue(rawImageVersion, { integer: true });

      return {
        id: item.id || combinationKey || `variant_item_${Date.now()}_${index}`,
        options: normalizedOptions,
        price: sanitizeNumericValue(item.price),
        sale_price: sanitizeNumericValue(item.sale_price),
        stock: sanitizeNumericValue(item.stock, { integer: true }),
        sku: item.sku || '',
        image_path: sanitizeImagePath(item.image_path || item.image || item.image_url || item.base_image || ''),
        image_version: normalizedImageVersion === null ? null : normalizedImageVersion
      };
    })
    .filter((item) => Object.keys(item.options).length > 0);
};

const UpdateProductModal = ({ isOpen, onClose, onProductUpdated, product }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    care_instructions: '',
    shipping_info: '',
    status: 'active',
    tags: '',
    seo_title: '',
    meta_description: '',
    base_price: '',
    sale_price: '',
    sku: '',
    quantity: 0
  });
  
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [originalImages, setOriginalImages] = useState([]); // Track original images when modal opens
  const [measurementImage, setMeasurementImage] = useState(null);
  const [existingMeasurementImage, setExistingMeasurementImage] = useState(null);
  const [deleteMeasurementImage, setDeleteMeasurementImage] = useState(false);
  const [measurementImageVersion, setMeasurementImageVersion] = useState(Date.now());
  const [imagesLoading, setImagesLoading] = useState(false);
  const [measurementImageLoading, setMeasurementImageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const measurementImageInputRef = useRef(null);
  const measurementImageFileRef = useRef(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Alert state management
  const [alert, setAlert] = useState({
    show: false,
    type: 'info',
    title: '',
    message: ''
  });

  // Variants state
  const [variants, setVariants] = useState([]);
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantValue, setNewVariantValue] = useState('');
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
  const [variantItems, setVariantItems] = useState([]);
  const [variantItemForm, setVariantItemForm] = useState({
    selectedValues: {},
    price: '',
    sale_price: '',
    stock: '',
    sku: '',
    image_version: '',
    image_path: ''
  });
  const [editingVariantItemIndex, setEditingVariantItemIndex] = useState(null);
  const existingImagesByVersion = useMemo(() => {
    return existingImages.reduce((acc, image) => {
      if (!image || typeof image.version === 'undefined' || image.version === null) {
        return acc;
      }
      const key = String(image.version);
      acc[key] = image;
      return acc;
    }, {});
  }, [existingImages]);

  const resolveVariantImageData = (imageVersion, imagePath) => {
    const normalizedVersion = sanitizeNumericValue(imageVersion, { integer: true });

    if (normalizedVersion !== null) {
      const key = String(normalizedVersion);
      const imageData = existingImagesByVersion[key];

      if (imageData) {
        return {
          url: imageData.url,
          label: `Image v${imageData.version}${imageData.isPrimary ? ' (Primary)' : ''}`,
          version: imageData.version,
          isPrimary: Boolean(imageData.isPrimary),
          source: 'library'
        };
      }
    }

    const cleanPath = sanitizeImagePath(imagePath);
    if (cleanPath) {
      const absoluteUrl = cleanPath.startsWith('http') ? cleanPath : `${IMAGE_BASE_URL}${cleanPath}`;
      return {
        url: absoluteUrl,
        label: cleanPath,
        version: null,
        isPrimary: false,
        source: 'custom'
      };
    }

    return {
      url: '',
      label: '',
      version: null,
      isPrimary: false,
      source: null
    };
  };

  const variantSelectionReady = useMemo(() => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return false;
    }

    return variants.every((variant) => {
      const selections = variantItemForm.selectedValues?.[variant.name];
      return Array.isArray(selections) && selections.length > 0;
    });
  }, [variants, variantItemForm.selectedValues]);

  const selectedVariantImageData = resolveVariantImageData(
    variantItemForm.image_version,
    variantItemForm.image_path
  );

  const editingVariantItem = editingVariantItemIndex !== null
    ? variantItems[editingVariantItemIndex]
    : null;

  // Alert helper functions
  const showAlert = (type, title, message) => {
    setAlert({
      show: true,
      type,
      title,
      message
    });
  };

  const hideAlert = () => {
    setAlert({
      show: false,
      type: 'info',
      title: '',
      message: ''
    });
  };

  const measurementImageUrl = useMemo(() => {
    if (!existingMeasurementImage) return null;
    const separator = existingMeasurementImage.includes('?') ? '&' : '?';
    if (existingMeasurementImage.startsWith('http')) {
      return `${existingMeasurementImage}${separator}t=${measurementImageVersion}`;
    }
    return `${IMAGE_BASE_URL}${existingMeasurementImage}${separator}t=${measurementImageVersion}`;
  }, [existingMeasurementImage, measurementImageVersion]);

  const measurementFilePreview = useMemo(() => {
    if (!measurementImage) return null;
    return URL.createObjectURL(measurementImage);
  }, [measurementImage]);

  useEffect(() => {
    return () => {
      if (measurementFilePreview) {
        URL.revokeObjectURL(measurementFilePreview);
      }
    };
  }, [measurementFilePreview]);

  useEffect(() => {
    if (!measurementImage) {
      setMeasurementImageLoading(false);
    }
  }, [measurementImage]);

  // Populate form when product prop changes
  useEffect(() => {
    let isSubscribed = true;

    if (isOpen && product) {
      const initialize = async () => {
        setFetchingData(true);
        try {
          await populateFormData(product);
        } catch (err) {
          console.error('Failed to populate product data:', err);
          if (isSubscribed) {
            setError('Failed to load product data. Please try again.');
          }
        } finally {
          if (isSubscribed) {
            setFetchingData(false);
          }
        }
      };

      initialize();
    }

    return () => {
      isSubscribed = false;
    };
  }, [isOpen, product]);

  // Populate form with product data
  const populateFormData = async (productData) => {
    if (!productData || typeof productData !== 'object') {
      setError('Invalid product data structure');
      return;
    }
    
    // Populate form with complete product data
    setFormData({
      name: productData.name || '',
      description: productData.description || '',
      care_instructions: productData.care_instructions || '',
      shipping_info: productData.shipping_info || '',
      status: productData.status || 'active',
      tags: productData.tags || '',
      seo_title: productData.seo_title || '',
      meta_description: productData.meta_description || '',
      base_price: productData.base_price || '',
      sale_price: productData.sale_price || '',
      sku: productData.sku || '',
      quantity: productData.quantity || 0,
      primary_image_version: productData.primary_image_version || 1
    });

    // Load existing variants
    if (productData.variants) {
      try {
        const parsedVariants = typeof productData.variants === 'string' 
          ? JSON.parse(productData.variants) 
          : productData.variants;

        let variantOptions = [];
        let variantCombinations = [];

        if (Array.isArray(parsedVariants)) {
          variantOptions = parsedVariants
            .filter(item => item && typeof item === 'object')
            .map(item => {
              const { items, variant_items, variantItems, ...rest } = item;
              const values = Array.isArray(rest.values)
                ? rest.values
                : rest.values
                  ? [rest.values]
                  : [];

              return {
                ...rest,
                name: typeof rest.name === 'string' ? rest.name : '',
                values: normalizeVariantValues(values)
              };
            })
            .filter(option => option.name);

          // Attempt to derive variant combinations if stored alongside options
          const embeddedItems = parsedVariants.find(item => Array.isArray(item?.items) || Array.isArray(item?.variant_items) || Array.isArray(item?.variantItems));
          if (embeddedItems) {
            variantCombinations = embeddedItems.items || embeddedItems.variant_items || embeddedItems.variantItems || [];
          }
        } else if (parsedVariants && typeof parsedVariants === 'object') {
          if (Array.isArray(parsedVariants.options)) {
            variantOptions = parsedVariants.options
              .filter(item => item && typeof item === 'object')
              .map(item => {
                const { items, variant_items, variantItems, ...rest } = item;
                const values = Array.isArray(rest.values)
                  ? rest.values
                  : rest.values
                    ? [rest.values]
                    : [];

                return {
                  ...rest,
                  name: typeof rest.name === 'string' ? rest.name : '',
                  values: normalizeVariantValues(values)
                };
              })
              .filter(option => option.name);
          }

          if (Array.isArray(parsedVariants.items)) {
            variantCombinations = parsedVariants.items;
          } else if (Array.isArray(parsedVariants.variantItems)) {
            variantCombinations = parsedVariants.variantItems;
          } else if (Array.isArray(parsedVariants.variant_items)) {
            variantCombinations = parsedVariants.variant_items;
          }
        }

        setVariants(variantOptions);
        const normalizedVariantItems = normalizeVariantItems(Array.isArray(variantCombinations) ? variantCombinations : []);
        setVariantItems(normalizedVariantItems);
        setVariantItemForm(prev => ({
          ...prev,
          selectedValues: variantOptions.reduce((acc, option) => {
            acc[option.name] = [];
            return acc;
          }, {})
        }));
        setEditingVariantItemIndex(null);
      } catch (e) {
        setVariants([]);
        setVariantItems([]);
      }
    } else {
      setVariants([]);
      setVariantItems([]);
      setVariantItemForm(prev => ({
        ...prev,
        selectedValues: {}
      }));
      setEditingVariantItemIndex(null);
    }

    // Load existing measurement image if it exists
    if (productData.measurement_image) {
      const path = productData.measurement_image;
      const separator = path.includes('?') ? '&' : '?';
      const resolved = path.startsWith('http') ? path : `${IMAGE_BASE_URL}${path}`;
      const timestamped = `${resolved}${separator}t=${Date.now()}`;
      setExistingMeasurementImage(timestamped);
      setMeasurementImageLoading(true);
      setMeasurementImageVersion(Date.now());
    } else {
      setExistingMeasurementImage(null);
      setMeasurementImageLoading(false);
      setMeasurementImageVersion(Date.now());
    }
    setMeasurementImage(null);
    setDeleteMeasurementImage(false);
    measurementImageFileRef.current = null;
    if (measurementImageInputRef.current) {
      measurementImageInputRef.current.value = '';
    }

    // Load existing images with the correct primary version
    await loadExistingImages(productData, productData.primary_image_version || 1);
  };

  useEffect(() => {
    if (!existingMeasurementImage && !measurementImage) {
      setMeasurementImageLoading(false);
    }
  }, [existingMeasurementImage, measurementImage]);

  useEffect(() => {
    if (!Array.isArray(variants) || variants.length === 0) {
      setVariantItems([]);
      setVariantItemForm(prev => ({
        ...prev,
        selectedValues: {}
      }));
      setEditingVariantItemIndex(null);
      return;
    }

    setVariantItems(prevItems => prevItems.filter(item => {
      if (!item || typeof item !== 'object' || !item.options) {
        return false;
      }

      const optionEntries = Object.entries(item.options);
      if (optionEntries.length !== variants.length) {
        return false;
      }

      return variants.every(variant => {
        const value = item.options[variant.name];
        if (typeof value === 'undefined' || value === null) {
          return false;
        }
        const normalizedValue = typeof value === 'string' ? value : String(value);
        return variant.values.includes(normalizedValue);
      });
    }));

    setVariantItemForm(prev => {
      const existingSelected = prev.selectedValues || {};
      const updatedSelected = variants.reduce((acc, variant) => {
        const currentValues = Array.isArray(existingSelected[variant.name])
          ? existingSelected[variant.name].filter(val => variant.values.includes(val))
          : [];

        acc[variant.name] = currentValues;
        return acc;
      }, {});

      return {
        ...prev,
        selectedValues: updatedSelected
      };
    });
  }, [variants]);

  // We'll use the product data passed as props directly
  // No need to fetch from API since we already have the data

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        name: '',
        description: '',
        care_instructions: '',
        shipping_info: '',
        status: 'active',
        tags: '',
        seo_title: '',
        meta_description: '',
        base_price: '',
        sale_price: '',
        sku: '',
        quantity: 0,
        primary_image_version: 1
      });
      setExistingImages([]);
      setOriginalImages([]);
      setVariants([]);
      setNewVariantName('');
      setNewVariantValue('');
      setSelectedVariantIndex(null);
      setVariantItems([]);
      setVariantItemForm({
        selectedValues: {},
        price: '',
        sale_price: '',
        stock: '',
        sku: '',
        image_version: '',
        image_path: ''
      });
      setEditingVariantItemIndex(null);
      setImages([]);
      setMeasurementImage(null);
      setExistingMeasurementImage(null);
      setDeleteMeasurementImage(false);
      measurementImageFileRef.current = null;
      if (measurementImageInputRef.current) {
        measurementImageInputRef.current.value = '';
      }
      setError('');
      setSuccess('');
      

    }
  }, [isOpen]);

  const loadExistingImages = async (product, primaryVersionOverride = null) => {
    if (!product || typeof product !== 'object' || !product.product_id) {
      console.error('Invalid product data for loading images:', product);
      setExistingImages([]);
      return;
    }

    setImagesLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${product.product_id}/assets`, {
        headers: {
          ...getAuthHeaders(),
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.success) {
          const assets = Array.isArray(data.images) ? data.images : [];
          const primaryVersion = primaryVersionOverride || data.primary_image_version || formData.primary_image_version || 1;

          const imagesWithUrls = assets.map((asset) => {
            const path = asset.path || '';
            const resolved = path
              ? (path.startsWith('http') ? path : `${IMAGE_BASE_URL}${path}`)
              : '';
            const baseUrl = resolved;
            const timestampedUrl = resolved
              ? `${resolved}${resolved.includes('?') ? '&' : '?'}t=${Date.now()}`
              : '';
            return {
              url: timestampedUrl,
              baseUrl: baseUrl,
              path: path || null,
              version: asset.version,
              isPrimary: asset.is_primary || asset.version === primaryVersion
            };
          });

          setExistingImages(imagesWithUrls);
          setOriginalImages(imagesWithUrls);

          if (data.measurement_image) {
            const separator = data.measurement_image.includes('?') ? '&' : '?';
            const resolved = data.measurement_image.startsWith('http')
              ? data.measurement_image
              : `${IMAGE_BASE_URL}${data.measurement_image}`;
            setExistingMeasurementImage(`${resolved}${separator}t=${Date.now()}`);
            setMeasurementImageVersion(Date.now());
            setMeasurementImageLoading(true);
          } else {
            setExistingMeasurementImage(null);
            setMeasurementImageLoading(false);
          }
          return;
        }
      }

      // Fallback: derive image URLs directly if assets endpoint fails or returns no data
      console.warn('Falling back to legacy image loading');
      const fallbackImages = [];
      const baseUrl = getConfigProductImageUrl(product);
      const primaryVersion = primaryVersionOverride || formData.primary_image_version || product?.primary_image_version || 1;

      for (let i = 1; i <= 6; i++) {
        const imageUrl = baseUrl ? baseUrl.replace(/_v\d+\.png/, `_v${i}.png`) : null;
        if (!imageUrl) continue;

        try {
          const headResponse = await fetch(imageUrl, {
            method: 'HEAD',
            headers: getAuthHeaders()
          });

          if (headResponse.ok) {
            const timestampedUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
            fallbackImages.push({
              url: timestampedUrl,
              baseUrl: imageUrl,
              path: null,
              version: i,
              isPrimary: i === primaryVersion
            });
          }
        } catch (_) {
          // ignore missing image
        }
      }

      setExistingImages(fallbackImages);
      setOriginalImages(fallbackImages);
      setMeasurementImageLoading(false);

    } catch (error) {
      console.error('Failed to load product assets:', error);
      setExistingImages([]);
      setOriginalImages([]);
      setExistingMeasurementImage(null);
      setMeasurementImageLoading(false);
    } finally {
      setImagesLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditorChange = (field) => (content) => {
    setFormData(prev => ({
      ...prev,
      [field]: content
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file count
    if (files.length > 6) {
      setError('Maximum 6 images allowed');
      return;
    }

    // Validate file size and type
    const validFiles = [];
    for (let file of files) {
      
      if (file.size > 1024 * 1024) { // 1MB
        setError(`Image "${file.name}" is too large. Maximum size is 1MB`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not a valid image file`);
        return;
      }
      
      validFiles.push(file);
    }

    setImages(validFiles);
    setError('');
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleMeasurementImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB
        setError(`Measurement image is too large. Maximum size is 1MB`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError(`"${file.name}" is not a valid image file`);
        return;
      }
      
      setMeasurementImage(file);
      measurementImageFileRef.current = file;
      setMeasurementImageLoading(true);
      setMeasurementImageVersion(Date.now());
      setDeleteMeasurementImage(false); // Unset delete flag if uploading new
      setError('');
      if (measurementImageInputRef.current) {
        measurementImageInputRef.current.value = '';
      }
    }
  };

  const removeMeasurementImage = () => {
    if (measurementImage) {
      setMeasurementImage(null);
    }
    if (existingMeasurementImage) {
      setDeleteMeasurementImage(true);
    }
    setMeasurementImageLoading(false);
    measurementImageFileRef.current = null;
    if (measurementImageInputRef.current) {
      measurementImageInputRef.current.value = '';
    }
  };

  const undoDeleteMeasurementImage = () => {
    setDeleteMeasurementImage(false);
    if (existingMeasurementImage) {
      setMeasurementImageLoading(true);
      setMeasurementImageVersion(Date.now());
    }
  };





  // Remove existing image
  const removeExistingImage = async (index) => {
    const imageToDelete = existingImages[index];
    if (!imageToDelete) {
      setError('Unable to find the selected image.');
      return;
    }
    
    try {
      // Call the delete API to remove the file from server
      const response = await fetch(`${API_BASE_URL}/admin/products/${product.product_id}/delete-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          version: imageToDelete.version
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showAlert('success', 'Success', 'Image deleted successfully');
        
        // Remove from local state
        const newImages = existingImages.filter((_, i) => i !== index);
        setExistingImages(newImages);
        setVariantItems(prevItems => prevItems.map(item => {
          if (!item) return item;
          const matchesVersion = typeof item.image_version !== 'undefined' && item.image_version !== null && item.image_version === imageToDelete.version;
          const matchesPath = item.image_path && (
            item.image_path === imageToDelete.baseUrl ||
            item.image_path === imageToDelete.path ||
            item.image_path === imageToDelete.url ||
            (imageToDelete.baseUrl && item.image_path === imageToDelete.baseUrl.split('?')[0])
          );

          if (matchesVersion || matchesPath) {
            return {
              ...item,
              image_version: null,
              image_path: ''
            };
          }
          return item;
        }));

        setVariantItemForm(prev => {
          if (prev.image_version === imageToDelete.version) {
            return {
              ...prev,
              image_version: '',
              image_path: ''
            };
          }
          return prev;
        });
        

      } else {
        console.error('Failed to delete image:', data.error);
        showAlert('error', 'Error', `Failed to delete image: ${data.error}`);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      showAlert('error', 'Error', 'Failed to delete image. Please try again.');
    }
  };

  // Set image as primary
  const setAsPrimaryImage = async (version) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${product.product_id}/set-primary-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          version: version
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showAlert('success', 'Success', `Image v${version} set as primary successfully`);
        
        // Update the local product data to reflect the new primary image
        setFormData(prev => ({
          ...prev,
          primary_image_version: version
        }));
        
        // Also update the product prop to reflect the new primary image
        if (product) {
          product.primary_image_version = version;
        }
        
        // Update existing images state to reflect new primary status
        setExistingImages(prevImages => 
          prevImages.map(img => ({
            ...img,
            isPrimary: img.version === version
          }))
        );
        
        // Reload images to refresh URLs and primary status with new timestamp
        await loadExistingImages(product, version);
        
        // Notify parent component that product was updated
        if (onProductUpdated) {
          onProductUpdated();
        }
      } else {
        console.error('Failed to set primary image:', data.error);
        showAlert('error', 'Error', `Failed to set primary image: ${data.error}`);
      }
    } catch (error) {
      console.error('Error setting primary image:', error);
      showAlert('error', 'Error', 'Failed to set primary image. Please try again.');
    }
  };

  // Variant management functions
  const addVariantName = () => {
    if (!newVariantName.trim()) {
      setError('Variant name is required');
      return;
    }

    // Check if variant name already exists
    if (variants.some(v => v.name.toLowerCase() === newVariantName.trim().toLowerCase())) {
      setError('Variant name already exists');
      return;
    }

    const newVariant = {
      name: newVariantName.trim(),
      values: []
    };

    setVariants(prev => [...prev, newVariant]);
    setNewVariantName('');
    setError('');
  };

  const addVariantValue = () => {
    if (selectedVariantIndex === null || selectedVariantIndex === '' || !newVariantValue.trim()) {
      setError('Please select a variant and enter a value');
      return;
    }

    const variant = variants[selectedVariantIndex];
    if (!variant) {
      setError('Selected variant not found');
      return;
    }

    if (variant.values.includes(newVariantValue.trim())) {
      setError('This value already exists for this variant');
      return;
    }

    setVariants(prev => prev.map((variant, index) => {
      if (index === selectedVariantIndex) {
        return {
          ...variant,
          values: [...variant.values, newVariantValue.trim()]
        };
      }
      return variant;
    }));

    setNewVariantValue('');
    setError('');
  };

  const removeVariant = (variantIndex) => {
    setVariants(prev => prev.filter((_, index) => index !== variantIndex));
    if (selectedVariantIndex === variantIndex) {
      setSelectedVariantIndex(null);
    }
  };

  const removeVariantValue = (variantIndex, valueIndex) => {
    setVariants(prev => prev.map((variant, index) => {
      if (index === variantIndex) {
        return {
          ...variant,
          values: variant.values.filter((_, vIndex) => vIndex !== valueIndex)
        };
      }
      return variant;
    }));
  };

  const toggleVariantItemValue = (variantName, value) => {
    if (!variantName || !value) {
      return;
    }

    setVariantItemForm(prev => {
      const currentSelections = prev.selectedValues || {};
      const currentValues = Array.isArray(currentSelections[variantName]) ? currentSelections[variantName] : [];
      let nextValues = [];

      if (editingVariantItemIndex !== null) {
        nextValues = [value];
      } else {
        nextValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
      }

      return {
        ...prev,
        selectedValues: {
          ...currentSelections,
          [variantName]: nextValues
        }
      };
    });
  };

  const handleVariantItemImageSelect = (value) => {
    const stringValue = value === null || typeof value === 'undefined' ? '' : String(value);

    if (!stringValue) {
      setVariantItemForm(prev => ({
        ...prev,
        image_version: '',
        image_path: ''
      }));
      return;
    }

    const imageData = existingImagesByVersion[stringValue];
    const imagePath = imageData ? sanitizeImagePath(imageData.baseUrl || imageData.url || imageData.path || '') : '';

    setVariantItemForm(prev => ({
      ...prev,
      image_version: stringValue,
      image_path: imagePath
    }));
  };

  const handleVariantItemFieldChange = (field, value) => {
    setVariantItemForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetVariantItemEditingState = () => {
    setEditingVariantItemIndex(null);
    setVariantItemForm(prev => ({
      ...prev,
      selectedValues: variants.reduce((acc, variant) => {
        acc[variant.name] = [];
        return acc;
      }, {}),
      price: '',
      sale_price: '',
      stock: '',
      sku: '',
      image_version: '',
      image_path: ''
    }));
  };

  const buildCombinationListFromSelection = () => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return [];
    }

    const selections = variants.map(variant => {
      const values = Array.isArray(variantItemForm.selectedValues?.[variant.name])
        ? variantItemForm.selectedValues[variant.name]
        : [];
      return {
        name: variant.name,
        values
      };
    });

    if (selections.some(selection => selection.values.length === 0)) {
      setError('Select at least one value for each variant to create combinations.');
      return null;
    }

    const results = [];

    const traverse = (index, current) => {
      if (index === selections.length) {
        results.push({ ...current });
        return;
      }

      const selection = selections[index];
      selection.values.forEach(val => {
        traverse(index + 1, {
          ...current,
          [selection.name]: val
        });
      });
    };

    traverse(0, {});
    return results;
  };

  const handleAddOrUpdateVariantItems = () => {
    if (!Array.isArray(variants) || variants.length === 0) {
      setError('Add at least one variant type before creating variant combinations.');
      return;
    }

    const combinations = buildCombinationListFromSelection();
    if (!combinations || combinations.length === 0) {
      return;
    }

    const priceValue = sanitizeNumericValue(variantItemForm.price);
    const salePriceValue = sanitizeNumericValue(variantItemForm.sale_price);

    if (priceValue !== null && salePriceValue !== null && salePriceValue > priceValue) {
      setError('Variant sale price cannot be greater than variant price.');
      return;
    }

    const stockValue = sanitizeNumericValue(variantItemForm.stock, { integer: true });
    const skuValue = variantItemForm.sku ? variantItemForm.sku.trim() : '';
    const imageVersionValue = sanitizeNumericValue(variantItemForm.image_version, { integer: true });
    const imageData = imageVersionValue !== null
      ? existingImagesByVersion[String(imageVersionValue)] || existingImagesByVersion[imageVersionValue]
      : null;
    const imagePath = imageData
      ? sanitizeImagePath(imageData.baseUrl || imageData.url || imageData.path || '')
      : sanitizeImagePath(variantItemForm.image_path);

    if (editingVariantItemIndex !== null) {
      if (combinations.length !== 1) {
        setError('Please select exactly one value per variant when editing a combination.');
        return;
      }

      const updatedOptions = combinations[0];
      const duplicateIndex = variantItems.findIndex((item, index) => {
        if (index === editingVariantItemIndex) {
          return false;
        }
        return buildVariantCombinationKey(item.options) === buildVariantCombinationKey(updatedOptions);
      });

      if (duplicateIndex !== -1) {
        setError('Another combination already uses this selection.');
        return;
      }

      const existingItem = variantItems[editingVariantItemIndex];
      const updatedItem = {
        id: existingItem?.id || buildVariantCombinationKey(updatedOptions) || `variant_item_${Date.now()}`,
        options: updatedOptions,
        price: priceValue,
        sale_price: salePriceValue,
        stock: stockValue,
        sku: skuValue,
        image_path: imagePath || '',
        image_version: imageVersionValue
      };

      setVariantItems(prev => prev.map((item, index) => (index === editingVariantItemIndex ? updatedItem : item)));
      resetVariantItemEditingState();
      setError('');
      return;
    }

    const duplicates = [];
    const newItems = combinations.reduce((acc, options) => {
      const key = buildVariantCombinationKey(options);
      const exists = variantItems.some(item => buildVariantCombinationKey(item.options) === key);
      if (exists) {
        duplicates.push(key);
        return acc;
      }

      acc.push({
        id: key || `variant_item_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        options,
        price: priceValue,
        sale_price: salePriceValue,
        stock: stockValue,
        sku: skuValue,
        image_path: imagePath || '',
        image_version: imageVersionValue
      });
      return acc;
    }, []);

    if (newItems.length === 0) {
      setError(duplicates.length > 0
        ? 'All selected combinations already exist. Please choose different values.'
        : 'Unable to add the selected variant combination.');
      return;
    }

    setVariantItems(prev => [...prev, ...newItems]);
    setVariantItemForm(prev => ({
      ...prev,
      price: '',
      sale_price: '',
      stock: '',
      sku: ''
    }));
    setError('');
  };

  const handleEditVariantItem = (index) => {
    if (index < 0 || index >= variantItems.length) {
      return;
    }

    const item = variantItems[index];
    if (!item || !item.options) {
      return;
    }

    const selectedValues = variants.reduce((acc, variant) => {
      const value = item.options[variant.name];
      acc[variant.name] = value ? [value] : [];
      return acc;
    }, {});

    setVariantItemForm(prev => ({
      ...prev,
      selectedValues,
      price: item.price === null || typeof item.price === 'undefined' ? '' : String(item.price),
      sale_price: item.sale_price === null || typeof item.sale_price === 'undefined' ? '' : String(item.sale_price),
      stock: item.stock === null || typeof item.stock === 'undefined' ? '' : String(item.stock),
      sku: item.sku || '',
      image_version: item.image_version === null || typeof item.image_version === 'undefined' ? '' : String(item.image_version),
      image_path: item.image_path || ''
    }));
    setEditingVariantItemIndex(index);
    setError('');
  };

  const handleDeleteVariantItem = (index) => {
    setVariantItems(prev => prev.filter((_, i) => i !== index));

    if (editingVariantItemIndex === index) {
      resetVariantItemEditingState();
    }
  };

  const handleCancelVariantItemEdit = () => {
    resetVariantItemEditingState();
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Product name is required');
      return false;
    }
    
    if (!formData.base_price || formData.base_price <= 0) {
      setError('Valid base price is required');
      return false;
    }
    
    if (formData.sale_price && formData.sale_price >= formData.base_price) {
      setError('Sale price must be less than base price');
      return false;
    }
    
    if (formData.sale_price && formData.sale_price <= 0) {
      setError('Sale price must be greater than 0');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token missing. Please login again.');
        setLoading(false);
        return;
      }

      // Prepare FormData
      const submitData = new FormData();
      
      // Add product ID for update
      submitData.append('product_id', product.product_id);
      
      // Add form fields
      submitData.append('name', formData.name);
      submitData.append('description', formData.description || '');
      submitData.append('care_instructions', formData.care_instructions || '');
      submitData.append('shipping_info', formData.shipping_info || '');
      submitData.append('base_price', formData.base_price);
      submitData.append('sale_price', formData.sale_price || '');
      submitData.append('quantity', formData.quantity || '0');
      submitData.append('status', formData.status);
      submitData.append('tags', formData.tags || '');
      submitData.append('seo_title', formData.seo_title || '');
      submitData.append('meta_description', formData.meta_description || '');
      submitData.append('sku', formData.sku || '');

      // Add new images if any
      if (images.length > 0) {
        images.forEach((image, index) => {
          submitData.append('images', image);
        });
      }

      // Add measurement image
      const measurementFile = measurementImage || measurementImageFileRef.current;
      if (measurementFile) {
        const fileName = measurementFile.name || 'measurement-image.png';
        submitData.append('measurement_image', measurementFile, fileName);
      }
      
      // Add delete measurement image flag
      if (deleteMeasurementImage) {
        submitData.append('delete_measurement_image', 'true');
      }

      // Add variants with combinations if available
      const variantPayload = variantItems.length > 0
        ? {
            options: variants,
            items: variantItems
          }
        : variants;

      submitData.append('variants', JSON.stringify(variantPayload));

      
      const prepareImageOrderData = () => {
        const orderData = [];
        
        // Only include reshuffle data if there are existing images that need reordering
        // For now, we'll let the individual delete and reshuffle APIs handle the reordering
        // This function will be used for future enhancements if needed
        
        return orderData;
      };

      // Get deleted image versions
      const getDeletedImageVersions = () => {
        // Track which images were deleted by comparing original vs current
        const originalVersions = originalImages.map(img => img.version);
        const currentVersions = existingImages.map(img => img.version);
        const deletedVersions = originalVersions.filter(version => !currentVersions.includes(version));
        return deletedVersions;
      };

      // Add image order and deletion data
      const imageOrderData = prepareImageOrderData();
      const deletedImagesData = getDeletedImageVersions();
      
      if (imageOrderData.length > 0) {
        submitData.append('image_order', JSON.stringify(imageOrderData));
      }
      
      if (deletedImagesData.length > 0) {
        submitData.append('deleted_images', JSON.stringify(deletedImagesData));
      }

      const response = await fetch(`${API_BASE_URL}/updateproduct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData,
      });

      // Try to parse response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        const textResponse = await response.text();
        setError('Server returned invalid response');
        setLoading(false);
        return;
      }

      if (response.ok) {
        if (data.success) {
          showAlert('success', 'Success', 'Product updated successfully!');
          
          // Notify parent
          if (onProductUpdated) {
            onProductUpdated();
          }
          
          // Clear form state
          setTimeout(() => {
            setImages([]);
            setError('');
            setSuccess('');
            onClose();
          }, 1500);
          
        } else {
          showAlert('error', 'Error', data.message || data.error || 'Product update failed');
        }
      } else {
        showAlert('error', 'Error', data.error || data.message || `Server error: ${response.status}`);
      }

    } catch (networkError) {
      showAlert('error', 'Error', `Network error: ${networkError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !fetchingData) {
      setImages([]);
      setError('');
      setSuccess('');
      setVariants([]);
      setNewVariantName('');
      setNewVariantValue('');
      setSelectedVariantIndex(null);
      setFetchingData(false);
      onClose();
    }
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-gray-900">Update Product</h2>
          <button
            onClick={handleClose}
                                      disabled={loading || fetchingData}
            className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter product name"
                disabled={loading || fetchingData}
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <Editor
                apiKey=""
                tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
                value={formData.description}
                onEditorChange={handleEditorChange('description')}
                init={{
                  base_url: '/tinymce/js/tinymce',
                  suffix: '.min',
                  license_key: 'gpl',
                  branding: false,
                  height: 300,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                disabled={loading || fetchingData}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Care Instructions
              </label>
              <Editor
                apiKey=""
                tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
                value={formData.care_instructions}
                onEditorChange={handleEditorChange('care_instructions')}
                init={{
                  base_url: '/tinymce/js/tinymce',
                  suffix: '.min',
                  license_key: 'gpl',
                  branding: false,
                  height: 250,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                disabled={loading || fetchingData}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Shipping & Order Processing
              </label>
              <Editor
                apiKey=""
                tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
                value={formData.shipping_info}
                onEditorChange={handleEditorChange('shipping_info')}
                init={{
                  base_url: '/tinymce/js/tinymce',
                  suffix: '.min',
                  license_key: 'gpl',
                  branding: false,
                  height: 250,
                  menubar: false,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                }}
                disabled={loading || fetchingData}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Original Price (MRP) *
              </label>
              <input
                type="number"
                name="base_price"
                value={formData.base_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                disabled={loading || fetchingData}
                required
              />
              <p className="text-xs text-gray-500 mt-1">This will show as strikethrough price if sale price is set</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sale Price (Current Price)
              </label>
              <input
                type="number"
                name="sale_price"
                value={formData.sale_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                disabled={loading || fetchingData}
              />
              <p className="text-xs text-gray-500 mt-1">Actual selling price (must be less than original price)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
                disabled={loading || fetchingData}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading || fetchingData}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SKU
              </label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Product SKU"
                disabled={loading || fetchingData}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="tag1, tag2, tag3"
                disabled={loading || fetchingData}
              />
            </div>
          </div>

          {/* SEO Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO Title
                </label>
                <input
                  type="text"
                  name="seo_title"
                  value={formData.seo_title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="SEO optimized title"
                  disabled={loading || fetchingData}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                </label>
                <textarea
                  name="meta_description"
                  value={formData.meta_description}
                  onChange={handleInputChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Meta description for search engines"
                  disabled={loading || fetchingData}
                />
              </div>
            </div>
          </div>

          {/* Variants Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Variants</h3>
            
            {/* Add Variant Name */}
            <div className="space-y-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newVariantName}
                  onChange={(e) => setNewVariantName(e.target.value)}
                  placeholder="Enter variant name (e.g., Color, Size)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading || fetchingData}
                />
                <button
                  type="button"
                  onClick={addVariantName}
                  disabled={loading || fetchingData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  Add Variant
                </button>
              </div>

              {/* Add Variant Values */}
              {variants.length > 0 && (
                <div className="flex space-x-2">
                  <select
                    value={selectedVariantIndex !== null ? selectedVariantIndex : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedVariantIndex(value === '' ? null : parseInt(value));
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading || fetchingData}
                  >
                    <option value="">Select variant to add values</option>
                    {variants.map((variant, index) => (
                      <option key={index} value={index}>{variant.name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={newVariantValue}
                    onChange={(e) => setNewVariantValue(e.target.value)}
                    placeholder="Enter value (e.g., Red, Large)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading || fetchingData}
                  />
                  <button
                    type="button"
                    onClick={addVariantValue}
                    disabled={loading || fetchingData || selectedVariantIndex === null}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    Add Value
                  </button>
                </div>
              )}

              {/* Display Variants */}
              {variants.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Current Variants:</h4>
                  {variants.map((variant, variantIndex) => (
                    <div key={variantIndex} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900">{variant.name}</h5>
                        <button
                          type="button"
                          onClick={() => removeVariant(variantIndex)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          disabled={loading || fetchingData}
                        >
                          Remove Variant
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {variant.values.map((value, valueIndex) => (
                          <span
                            key={valueIndex}
                            className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-md"
                          >
                            {value}
                            <button
                              type="button"
                              onClick={() => removeVariantValue(variantIndex, valueIndex)}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                              disabled={loading || fetchingData}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        {variant.values.length === 0 && (
                          <span className="text-sm text-gray-500 italic">No values added yet</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {variants.length > 0 && (
                <div className="mt-6 space-y-4">
                  <div className="flex flex-col gap-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {editingVariantItem ? 'Edit Variant Combination' : 'Create Variant Combinations'}
                    </h4>
                    <p className="text-xs text-gray-500">
                      Select at least one value for every variant, then set the price, sale price, stock, SKU, and image to link them together.
                      {!editingVariantItem && ' Selecting multiple values per variant will create every possible combination.'}
                    </p>
                  </div>

                  <div className="space-y-3">
                    {variants.map((variant) => {
                      const selections = Array.isArray(variantItemForm.selectedValues?.[variant.name])
                        ? variantItemForm.selectedValues[variant.name]
                        : [];

                      return (
                        <div key={variant.name} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">{variant.name}</span>
                            <span className="text-xs text-gray-500">{selections.length} selected</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {variant.values.length > 0 ? (
                              variant.values.map((value) => {
                                const isSelected = selections.includes(value);
                                return (
                                  <button
                                    key={value}
                                    type="button"
                                    onClick={() => toggleVariantItemValue(variant.name, value)}
                                    disabled={loading || fetchingData}
                                    className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                                      isSelected
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                                    }`}
                                  >
                                    {value}
                                  </button>
                                );
                              })
                            ) : (
                              <span className="text-xs text-gray-500 italic">
                                Add values to {variant.name} to use it in combinations.
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Variant Price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={variantItemForm.price}
                        onChange={(e) => handleVariantItemFieldChange('price', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Use base price"
                        disabled={loading || fetchingData}
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank to inherit the product base price.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Variant Sale Price</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={variantItemForm.sale_price}
                        onChange={(e) => handleVariantItemFieldChange('sale_price', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Optional"
                        disabled={loading || fetchingData}
                      />
                      <p className="text-xs text-gray-500 mt-1">Must be lower than the variant price if provided.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={variantItemForm.stock}
                        onChange={(e) => handleVariantItemFieldChange('stock', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        disabled={loading || fetchingData}
                      />
                      <p className="text-xs text-gray-500 mt-1">Set the inventory for this combination.</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                      <input
                        type="text"
                        value={variantItemForm.sku}
                        onChange={(e) => handleVariantItemFieldChange('sku', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Optional SKU override"
                        disabled={loading || fetchingData}
                      />
                      <p className="text-xs text-gray-500 mt-1">Overrides the main SKU when this variant is selected.</p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Variant Image</label>
                      <select
                        value={
                          variantItemForm.image_version === null ||
                          typeof variantItemForm.image_version === 'undefined' ||
                          variantItemForm.image_version === ''
                            ? ''
                            : String(variantItemForm.image_version)
                        }
                        onChange={(e) => handleVariantItemImageSelect(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={loading || fetchingData || existingImages.length === 0}
                      >
                        <option value="">No linked image</option>
                        {existingImages.length === 0 && (
                          <option value="" disabled>
                            Upload product images to link them here
                          </option>
                        )}
                        {existingImages.map((imageData, index) => (
                          <option
                            key={imageData.version ?? `image-${index}`}
                            value={String(imageData.version)}
                          >
                            {`Image v${imageData.version}${imageData.isPrimary ? ' (Primary)' : ''}`}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Choose from the images already uploaded for this product. Selecting one stores the link for this variant combination.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custom Image Path or URL (optional)
                      </label>
                      <input
                        type="text"
                        value={variantItemForm.image_path}
                        onChange={(e) => handleVariantItemFieldChange('image_path', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="/uploads/products/... or https://example.com/image.jpg"
                        disabled={loading || fetchingData}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leave blank when linking to an uploaded image. You can paste an external URL if needed.
                      </p>
                    </div>

                    {selectedVariantImageData.url && (
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <img
                            src={selectedVariantImageData.url}
                            alt="Variant preview"
                            className="h-14 w-14 object-cover rounded-md border border-gray-200"
                          />
                          <div>
                            <p className="text-sm text-gray-700">{selectedVariantImageData.label}</p>
                            {selectedVariantImageData.source === 'library' && selectedVariantImageData.isPrimary && (
                              <p className="text-xs text-green-600 mt-1">Primary product image</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <span className="text-xs text-gray-500">
                      {variantSelectionReady
                        ? editingVariantItem
                          ? 'Ready to update this variant combination.'
                          : 'Ready to add the selected combinations.'
                        : 'Choose at least one value for every variant to continue.'}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleAddOrUpdateVariantItems}
                        disabled={!variantSelectionReady || loading || fetchingData}
                        className={`px-4 py-2 rounded-md text-white transition-colors ${
                          !variantSelectionReady || loading || fetchingData
                            ? 'bg-blue-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {editingVariantItem ? 'Update Combination' : 'Add Combination(s)'}
                      </button>
                      {editingVariantItem && (
                        <button
                          type="button"
                          onClick={handleCancelVariantItemEdit}
                          className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                          disabled={loading || fetchingData}
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Current Variant Combinations</h4>
                    {variantItems.length === 0 ? (
                      <p className="text-xs text-gray-500">
                        No variant combinations yet. Start by selecting values and clicking “Add Combination(s)”.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {variantItems.map((item, index) => {
                          const combinationImage = resolveVariantImageData(item.image_version, item.image_path);
                          const options = Object.entries(item.options || {});

                          return (
                            <div
                              key={item.id || index}
                              className={`rounded-lg border p-4 transition-colors ${
                                editingVariantItemIndex === index
                                  ? 'border-blue-400 bg-blue-50'
                                  : 'border-gray-200 bg-white'
                              }`}
                            >
                              <div className="flex flex-wrap items-center gap-2 mb-3">
                                {options.length > 0 ? (
                                  options.map(([name, value]) => (
                                    <span
                                      key={`${name}-${value}`}
                                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-md"
                                    >
                                      {name}: {value}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-500 italic">No variant selections</span>
                                )}
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-700">
                                <div>
                                  <span className="block text-[11px] uppercase text-gray-400">Price</span>
                                  {typeof item.price === 'number' && !Number.isNaN(item.price) ? item.price : '—'}
                                </div>
                                <div>
                                  <span className="block text-[11px] uppercase text-gray-400">Sale Price</span>
                                  {typeof item.sale_price === 'number' && !Number.isNaN(item.sale_price) ? item.sale_price : '—'}
                                </div>
                                <div>
                                  <span className="block text-[11px] uppercase text-gray-400">Stock</span>
                                  {typeof item.stock === 'number' && !Number.isNaN(item.stock) ? item.stock : '—'}
                                </div>
                                <div>
                                  <span className="block text-[11px] uppercase text-gray-400">SKU</span>
                                  {item.sku ? item.sku : '—'}
                                </div>
                              </div>

                              <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                {combinationImage.url ? (
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={combinationImage.url}
                                      alt="Variant combination"
                                      className="h-14 w-14 object-cover rounded-md border border-gray-200"
                                    />
                                    <div>
                                      <p className="text-xs text-gray-600">{combinationImage.label}</p>
                                      {combinationImage.source === 'library' && combinationImage.isPrimary && (
                                        <p className="text-[11px] text-green-600">Primary product image</p>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-500">No image linked to this combination.</p>
                                )}

                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleEditVariantItem(index)}
                                    className="px-3 py-1 rounded-md border border-blue-500 text-blue-600 text-sm hover:bg-blue-50 transition-colors"
                                    disabled={loading || fetchingData}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteVariantItem(index)}
                                    className="px-3 py-1 rounded-md border border-red-500 text-red-600 text-sm hover:bg-red-50 transition-colors"
                                    disabled={loading || fetchingData}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Image Upload */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Images</h3>
            
            {/* Existing Images */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Current Images ({existingImages.length})
                </label>

              </div>
                                <p className="text-xs text-gray-500 mb-2">
                    Tip: Click "Set Primary" to choose which image displays in product cards • Click the × button to delete images
                  </p>
              <div className="relative">
                {imagesLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                )}
                {existingImages.length > 0 ? (
                  <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${imagesLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                    {existingImages.map((imageData, index) => (
                      <div 
                        key={index} 
                        className="relative group"
                      >
                        <img
                          src={imageData.url}
                          alt={`Current ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                        <div className="w-full h-24 bg-gray-100 rounded-lg hidden items-center justify-center border-2 border-gray-200">
                          <ImageIcon className="h-6 w-6 text-gray-400" />
                        </div>
                        
                        {/* Delete button */}
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={loading || imagesLoading}
                        >
                          ×
                        </button>
                        
                        {/* Set as Primary button */}
                        <button
                          type="button"
                          onClick={() => setAsPrimaryImage(imageData.version)}
                          className={`absolute bottom-1 right-1 px-2 py-1 text-xs rounded transition-opacity ${
                            imageData.isPrimary 
                              ? 'bg-green-600 text-white opacity-100' 
                              : 'bg-blue-500 text-white opacity-0 group-hover:opacity-100 hover:bg-blue-600'
                          }`}
                          disabled={loading || imagesLoading}
                          title={imageData.isPrimary ? 'This is the primary image' : 'Set as primary image'}
                        >
                          {imageData.isPrimary ? '✓ Primary' : 'Set Primary'}
                        </button>
                        
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                          v{imageData.version}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 ${imagesLoading ? 'opacity-50' : ''}`}>
                    <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No existing images found</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload New Images (Max 6, 1MB each)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload-update"
                    disabled={loading || fetchingData}
                  />
                  <label
                    htmlFor="image-upload-update"
                    className="cursor-pointer flex flex-col items-center space-y-2"
                  >
                    <Upload className="h-8 w-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Click to upload new images (will replace existing)
                    </span>
                      <span className="text-xs text-gray-500">
                        PNG, JPG, JPEG, GIF, WEBP up to 1MB each
                      </span>
                  </label>
                </div>
              </div>

              {/* New Image Preview */}
              {images.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Images ({images.length}/6)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Tip: Click the × button to delete images
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {images.map((image, index) => (
                      <div 
                        key={index} 
                        className="relative group"
                      >
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          disabled={loading}
                        >
                          ×
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                          {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Measurement Image Upload */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Measurement Image (Optional)</h3>
            
            <div className="space-y-4">
              {/* Existing Measurement Image */}
              {existingMeasurementImage && !deleteMeasurementImage && !measurementImage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Measurement Image
                  </label>
                  <div className="relative inline-block">
                    {measurementImageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg z-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                    {measurementImageUrl ? (
                      <img
                        src={measurementImageUrl}
                        alt="Current measurement"
                        className="w-48 h-48 object-cover rounded-lg border-2 border-gray-200"
                        onLoad={() => setMeasurementImageLoading(false)}
                        onError={() => {
                          setMeasurementImageLoading(false);
                          setExistingMeasurementImage(null);
                        }}
                      />
                    ) : (
                      <div className="w-48 h-48 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500 text-sm">
                        No measurement image available
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={removeMeasurementImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Deleted Measurement Image Notice */}
              {deleteMeasurementImage && !measurementImage && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800 mb-2">
                    Measurement image will be deleted when you save changes.
                  </p>
                  <button
                    type="button"
                    onClick={undoDeleteMeasurementImage}
                    className="text-sm text-blue-600 hover:text-blue-700 underline"
                    disabled={loading}
                  >
                    Undo delete
                  </button>
                </div>
              )}

              {/* Upload New Measurement Image */}
              {(!existingMeasurementImage || deleteMeasurementImage || measurementImage) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {measurementImage ? 'New Measurement Image' : 'Upload Measurement Chart'}
                  </label>
                  {!measurementImage && (
                    <>
                      <p className="text-xs text-gray-600 mb-3">
                        Upload a size guide or measurement chart for customers (Max 1MB)
                      </p>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleMeasurementImageChange}
                          className="hidden"
                          id="measurement-image-upload-update"
                          ref={measurementImageInputRef}
                          disabled={loading}
                        />
                        <label
                          htmlFor="measurement-image-upload-update"
                          className="cursor-pointer flex flex-col items-center space-y-2"
                        >
                          <Upload className="h-8 w-8 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            Click to upload measurement image
                          </span>
                          <span className="text-xs text-gray-500">
                            PNG, JPG, JPEG, GIF, WEBP up to 1MB
                          </span>
                        </label>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* New Measurement Image Preview */}
              {measurementImage && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Measurement Image Preview
                  </label>
                  <div className="relative inline-block">
                    {measurementImageLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg z-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                    {measurementFilePreview && (
                      <img
                        src={measurementFilePreview}
                        alt="New measurement preview"
                        className="w-48 h-48 object-cover rounded-lg border-2 border-blue-300"
                        onLoad={() => setMeasurementImageLoading(false)}
                        onError={() => {
                          setMeasurementImageLoading(false);
                          setMeasurementImage(null);
                          measurementImageFileRef.current = null;
                          setError('Unable to preview measurement image. Please try a different file.');
                        }}
                      />
                    )}
                    <button
                      type="button"
                      onClick={removeMeasurementImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="border-t pt-6 flex space-x-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading || fetchingData}
              className="flex-1 bg-gray-200 text-gray-800 py-3 px-4 rounded-md hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || fetchingData}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center transition-colors"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                'Update Product'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Alert Component */}
      <Alert
        show={alert.show}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={hideAlert}
        autoClose={alert.type === 'success' || alert.type === 'error'}
        autoCloseDelay={3000}
      />
    </div>
  );
};

export default UpdateProductModal;